import { inject, Injectable } from '@angular/core';
import { KILLER_WEB_SOCKET_CONFIG } from './config';
import {
  CLIENT_PROTOCOL_NAME,
  KIOSK_PROTOCOL_NAME,
} from '@pool-league/shared-data-killer-ws-api';
import { WebSocketSubject } from 'rxjs/webSocket';
import { Observable, tap } from 'rxjs';
import {
  ClientOutMessage,
  KioskOutMessage,
} from '@pool-league/shared-util-ws-killer';

@Injectable()
export abstract class KillerWebSocketApiClient<
  OutMessage extends ClientOutMessage | KioskOutMessage,
> {
  readonly #maxIdleTime = 8 * 60 * 1000;
  #idleTimeout: number | undefined;

  readonly #config = inject(KILLER_WEB_SOCKET_CONFIG);

  #socket: WebSocketSubject<unknown> | undefined;

  protected connectInternal(
    protocol: [typeof KIOSK_PROTOCOL_NAME, string],
    sessionId: string,
  ): Observable<unknown>;
  protected connectInternal(
    protocol: [typeof CLIENT_PROTOCOL_NAME],
    sessionId: string,
  ): Observable<unknown>;
  protected connectInternal(
    protocol: string[],
    sessionId: string,
  ): Observable<unknown> {
    if (this.#socket) {
      console.warn('connect called with existing connection present');
      this.disconnect();
    }

    this.#socket = new WebSocketSubject({
      url: `${this.#config.endpoint}?sessionId=${sessionId}`,
      protocol,
    });

    return this.#socket.pipe(
      tap({
        next: (msg: unknown) => {
          console.debug('WS msg', msg);
          this.#resetIdleTimeout();
        },
        error: (error) => {
          console.error('WS error', error);
          this.#socket = undefined;
          this.#clearIdleTimeout();
        },
        complete: () => {
          console.debug('WS disconnected');
          this.#socket = undefined;
          this.#clearIdleTimeout();
        },
      }),
    );
  }

  disconnect() {
    this.#socket?.complete();
    this.#socket = undefined;
    this.#clearIdleTimeout();
  }

  send(payload: OutMessage): void {
    if (this.#socket) {
      this.#socket.next(payload);
      this.#resetIdleTimeout();
    } else {
      console.warn('Cannot send WS message: not connected', payload);
    }
  }

  #clearIdleTimeout() {
    if (this.#idleTimeout !== undefined) {
      clearTimeout(this.#idleTimeout);
    }
  }

  #resetIdleTimeout() {
    this.#clearIdleTimeout();
    this.#idleTimeout = setTimeout(() => {
      console.log(`Disconnecting as idle for ${this.#maxIdleTime}ms`);
      this.#idleTimeout = undefined;
      this.disconnect();
    }, this.#maxIdleTime);
  }
}
